import React, {useEffect, useRef, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

import {validateText} from "redux/actions/settingsActions";
import {postTemplatesPreview} from "redux/api/messagesAPI";
import {useFeatureFlags} from "utilities/featureUtil";
import {useServiceAccounts} from "hooks/useServiceAccounts";
import {useTemplateSearch} from "hooks/useTemplateSearch";

import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import EmailInput from "connectui/component/input/text/type/email/EmailInput";
import TextInput from "connectui/component/input/text/type/TextInput";
import Container from "connectui/component/layout/Container";
import WhatsappTemplateSelector from "ui/component/inbox/conversations/input/WhatsappTemplateSelector";
import NoteInput from "./NoteInput";
import TemplatesMenu from "./TemplatesMenu";

const useStyles = (theme) =>
  buildStyles({
    input: {
      flex: 1,
      width: "100%",
      height: "100%",
      padding: theme.spacing(0, 4),
      "&.disabled": {
        backgroundColor: theme.colors.white,
        cursor: "not-allowed",
      },
    },
  });

const ConversationInputComponent = ({
  guestId,
  channel,
  inputText,
  setInputText,
  setNoteText,
  componentProps = {},
  insertedText,
  clearInsertedText,
  templatesData = {},
  setTemplatesData,
}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {cannedResponses} = useFeatureFlags();

  const refs = useRef({
    initialEmailUpdate: true,
    emailContentChanged: false,
  });
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const emailInputRef = useRef(null);

  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );

  const isEmail = channel === "email";
  const editor = isEmail
    ? emailInputRef.current?.getEditor()
    : inputRef.current;

  const getText = () => {
    if (!editor) return "";
    return isEmail ? editor.getText() : editor.value;
  };

  const getCursor = () => {
    if (!editor) return 0;
    return isEmail
      ? (editor.getSelection()?.index ?? 0)
      : editor.selectionStart;
  };

  const messageContent = getText();
  const cursorPosition = getCursor();

  const {
    open: templatesOpen,
    loading: loadingTemplates,
    templates = [],
    buttonClicked,
  } = templatesData;

  const {
    fullEmailInput,
    emailContent,
    setEmailContent,
    whatsappData,
    setWhatsappData,
  } = componentProps;

  const insertText = useCallback(
    (text, start, end) => {
      if (isEmail) {
        editor.insertText(start, text);
        editor.setSelection(end, 0);
      } else {
        const val = editor.value;
        const updated = val.slice(0, start) + text + val.slice(start);
        if (inputRef.current) {
          inputRef.current.value = updated;
          inputRef.current.setSelectionRange(end, end);
        }
        setInputText(updated);
      }
    },
    [isEmail, editor, setInputText],
  );

  const handleSelectResponse = (response, enterKey, currentState) => {
    dispatch(
      validateText({
        guest_id: guestId,
        replace_text: true,
        text: response.text,
        format: "text",
        onSuccess: (filteredResponse) => {
          handleInsertText(
            filteredResponse.replaced_text,
            enterKey,
            currentState,
          );
        },
      }),
    );
  };

  const {
    templateState,
    openTemplates,
    closeTemplates,
    handleKeyDown,
    onSelect,
  } = useTemplateSearch(
    templates,
    messageContent,
    cursorPosition,
    templatesOpen,
    setTemplatesData,
    handleSelectResponse,
    emailContent,
    inputText,
    insertText,
    menuRef,
  );
  const {search, results, selectedIdx, disabled} = templateState;

  const showTemplates =
    templatesOpen && cannedResponses && !disabled && results.length > 0;

  const inputProps = isMobile
    ? {
        style: {
          width: "100%",
          minHeight: "24px",
          maxHeight: "120px",
          resize: "none",
          color: theme.colors.textPrimary,
          backgroundColor: theme.colors.white,
          cursor: "text",
          ...theme.typography.textXS.regular,
          fontSize: "13px",
        },
        onInput: (e) => {
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        },
      }
    : {
        style: {
          width: "100%",
          height: "100%",
          resize: "none",
          color: theme.colors.textPrimary,
          backgroundColor: theme.colors.white,
          cursor: "text",
          ...theme.typography.textXS.regular,
        },
      };

  useServiceAccounts(setWhatsappData);

  useEffect(() => {
    if (buttonClicked) {
      // Focus on the input
      if (editor) {
        editor.focus();
      }

      // Insert a "/" if missing
      if (cursorPosition === 0) {
        insertText("/", 0, 1);
      } else if (messageContent.charAt(cursorPosition - 1) !== "/") {
        insertText("/", cursorPosition, cursorPosition + 1);
      }

      openTemplates();
      setTemplatesData({...templatesData, buttonClicked: false});
    }
  }, [buttonClicked]);

  useEffect(() => {
    // Reset data when switching conversations
    refs.current.initialEmailUpdate = true;
    refs.current.emailContentChanged = false;
    if (whatsappData.accountId) {
      getTemplatesPreview(whatsappData.accountId);
    }
  }, [guestId]);

  useEffect(() => {
    if (!refs.current.emailContentChanged) {
      setEmailContent((prev) => ({
        ...prev,
        html: `${inputText}${user_profile.email_signature ? `<p><br></p>${user_profile.email_signature}` : ""}`,
      }));
    }
  }, [inputText, setEmailContent]);

  useEffect(() => {
    // Insert boarding pass link
    if (!insertedText) return;

    let position = cursorPosition;
    if (editor) {
      editor.focus();
    }
    if (isEmail) {
      position = editor?.getSelection()?.index ?? 0;
    }
    insertText(insertedText, position, position + insertedText.length);
    clearInsertedText();
  }, [insertedText]);

  useEffect(() => {
    if (whatsappData.accountId) {
      getTemplatesPreview(whatsappData.accountId);
    }
  }, [whatsappData.accountId]);

  const getTemplatesPreview = (accountId) => {
    if (["chat", "profile"].includes(guestId)) return;
    postTemplatesPreview({
      body: {
        template_type: "whatsapp",
        enso_key: user_profile.enso_key,
        guest_id: guestId,
        service_id: accountId,
      },
    }).then((result) => {
      setWhatsappData((prev) => ({...prev, preview: result}));
    });
  };

  const getInputHeight = () => {
    if (isEmail) {
      if (fullEmailInput) return "430px";
      else return "100px";
    }
    if (channel === "whatsapp" && whatsappData.selected) {
      return "250px";
    }
    if (channel === "note") {
      return "215px";
    }
    return "140px";
  };

  // Run after 'validateText' action dispatch
  const handleInsertText = (replaced_text, enterKey, currentState) => {
    const currentSearch = currentState?.search || search;
    const currentCursorPos = currentState?.cursorPosition || cursorPosition;
    const currentMessageContent =
      currentState?.messageContent || messageContent;

    if (isEmail) {
      const offset = enterKey ? 2 : 1;
      const startIndex = currentCursorPos - currentSearch.length - 1;
      if (editor) {
        editor.deleteText(startIndex, currentSearch.length + offset);
        insertText(
          replaced_text,
          startIndex,
          startIndex + replaced_text.length,
        );
      }
    } else {
      // Find the last "/" before cursor
      const lastSlashIndex = currentMessageContent.lastIndexOf(
        "/",
        currentCursorPos - 1,
      );

      if (lastSlashIndex !== -1) {
        // Replace text after last "/"
        const beforeSlash = currentMessageContent.substring(0, lastSlashIndex);
        const afterCursor = currentMessageContent.substring(currentCursorPos);
        const newText = beforeSlash + replaced_text + afterCursor;

        setInputText(newText);

        const newCursorPos = lastSlashIndex + replaced_text.length;
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }
    }
  };

  const handleEmailChange = (newContent) => {
    if (refs.current.initialEmailUpdate) {
      refs.current.initialEmailUpdate = false;
      const prev = JSON.stringify(emailContent);
      const next = JSON.stringify(newContent);
      if (prev === next) return;
    }
    refs.current.emailContentChanged = true;
    setEmailContent((prev) => ({...prev, ...newContent}));
  };

  const getInput = () => {
    const emailAnchorEl = emailInputRef.current?.getEditorRoot();
    const inputAnchorEl = inputRef.current;

    if (channel === "whatsapp" && !whatsappData.freeText)
      return (
        <WhatsappTemplateSelector
          data={whatsappData}
          setData={setWhatsappData}
          inputText={inputText}
          onChange={setInputText}
        />
      );

    if (channel === "note")
      return <NoteInput guestId={guestId} setNoteText={setNoteText} />;

    return (
      <>
        <TemplatesMenu
          open={showTemplates}
          menuRef={menuRef}
          skeleton={{enabled: loadingTemplates}}
          searchText={search}
          offset={emailAnchorEl ? 16 : 0}
          data={search ? results : templates}
          selectedIndex={selectedIdx}
          anchorEl={emailAnchorEl ?? inputAnchorEl}
          onClose={closeTemplates}
          onSelect={onSelect}
        />
        {isEmail ? (
          <EmailInput
            ref={emailInputRef}
            imagePath={`${user_profile.enso_key}/messages/${guestId}/email_images`}
            email={emailContent}
            onKeyDown={handleKeyDown}
            onChange={handleEmailChange}
          />
        ) : (
          <TextInput
            as={"textarea"}
            ref={inputRef}
            className={classes.input}
            inputProps={{
              value: inputText,
              onChange: (e) => setInputText(e.target.value),
              onKeyDown: handleKeyDown,
              placeholder:
                cannedResponses && !isMobile
                  ? t("inbox.conversations.input.placeholder.templates")
                  : t("inbox.conversations.input.placeholder.no-templates"),
              ...inputProps,
            }}
          />
        )}
      </>
    );
  };

  if (isMobile) return getInput();
  return (
    <Container
      sx={{
        width: "100%",
        height: getInputHeight(),
      }}
    >
      {getInput()}
    </Container>
  );
};

export default ConversationInputComponent;
